import posthog from "posthog-js";

import { logErrorToBackend } from "@/lib/error-logger";
import { toast } from "@/lib/use-toast";

const WINDOW_MS = 10_000;
const MAX_PER_PATH_PER_WINDOW = 150;
const MAX_PER_USER_PER_WINDOW = 300;
const TOAST_DEDUP_MS = 30_000;
const POSTHOG_DEDUP_MS = 60_000;
const SLACK_DEDUP_MS = 5 * 60_000;
const SWEEP_EVERY = 64;

/**
 * Header set by this module on its own Slack self-alert so api-config's
 * beforeRequest hook can skip the rate-limit check for that one request.
 * Without it, a blanket-scope trip would block the very POST /errors/log
 * that's trying to notify us about the trip.
 */
export const RATE_LIMIT_BYPASS_HEADER = "x-decoda-rate-limit-bypass";

const pathBuckets = new Map<string, number[]>();
const blanketBucket: number[] = [];
const toastedAt = new Map<string, number>();
const posthoggedAt = new Map<string, number>();
const slackedAt = new Map<string, number>();
let sweepCounter = 0;

export type RateLimitScope = "path" | "blanket";

export class ClientRateLimitError extends Error {
  readonly name = "ClientRateLimitError";
  readonly scope: RateLimitScope;
  readonly pathKey: string;
  readonly retryAfterMs: number;

  constructor(scope: RateLimitScope, pathKey: string, retryAfterMs: number) {
    super(`Client rate limit tripped (${scope}): ${pathKey}`);
    this.scope = scope;
    this.pathKey = pathKey;
    this.retryAfterMs = retryAfterMs;
  }
}

export type RateLimitDecision =
  | { allowed: true }
  | { allowed: false; scope: RateLimitScope; retryAfterMs: number };

function pruneBucket(bucket: number[], cutoff: number): void {
  while (bucket.length > 0 && bucket[0] < cutoff) {
    bucket.shift();
  }
}

export function checkAndRecord(pathKey: string): RateLimitDecision {
  const now = Date.now();
  const cutoff = now - WINDOW_MS;

  pruneBucket(blanketBucket, cutoff);
  if (blanketBucket.length >= MAX_PER_USER_PER_WINDOW) {
    return {
      allowed: false,
      scope: "blanket",
      retryAfterMs: blanketBucket[0] + WINDOW_MS - now,
    };
  }

  let pathBucket = pathBuckets.get(pathKey);
  if (!pathBucket) {
    pathBucket = [];
    pathBuckets.set(pathKey, pathBucket);
  }
  pruneBucket(pathBucket, cutoff);
  if (pathBucket.length >= MAX_PER_PATH_PER_WINDOW) {
    return {
      allowed: false,
      scope: "path",
      retryAfterMs: pathBucket[0] + WINDOW_MS - now,
    };
  }

  blanketBucket.push(now);
  pathBucket.push(now);

  sweepCounter += 1;
  if (sweepCounter >= SWEEP_EVERY) {
    sweepCounter = 0;
    pathBuckets.forEach((arr, key) => {
      pruneBucket(arr, cutoff);
      if (arr.length === 0) pathBuckets.delete(key);
    });
    pruneDedupMap(toastedAt, now - TOAST_DEDUP_MS);
    pruneDedupMap(posthoggedAt, now - POSTHOG_DEDUP_MS);
    pruneDedupMap(slackedAt, now - SLACK_DEDUP_MS);
  }

  return { allowed: true };
}

function pruneDedupMap(map: Map<string, number>, cutoff: number): void {
  map.forEach((ts, key) => {
    if (ts < cutoff) map.delete(key);
  });
}

export function reportRateLimitTrip(
  scope: RateLimitScope,
  pathKey: string,
  pathname: string,
): void {
  const dedupKey = scope === "path" ? `path:${pathKey}` : "blanket";
  const now = Date.now();

  if ((toastedAt.get(dedupKey) ?? 0) + TOAST_DEDUP_MS < now) {
    toastedAt.set(dedupKey, now);
    toast({
      variant: "error",
      title: "Something went wrong",
      description: "Please refresh the page.",
      id: `client-rate-limit:${dedupKey}`,
    });
  }

  if ((posthoggedAt.get(dedupKey) ?? 0) + POSTHOG_DEDUP_MS < now) {
    posthoggedAt.set(dedupKey, now);
    posthog.capture("client_rate_limit_tripped", {
      scope,
      pathKey,
      pathname,
    });
  }

  if ((slackedAt.get(dedupKey) ?? 0) + SLACK_DEDUP_MS < now) {
    slackedAt.set(dedupKey, now);
    void logErrorToBackend(
      new Error(`Client rate limit tripped (${scope}): ${pathKey}`),
      "client-rate-limiter",
      {
        scope,
        pathKey,
        pathname,
        windowMs: WINDOW_MS,
        threshold:
          scope === "path" ? MAX_PER_PATH_PER_WINDOW : MAX_PER_USER_PER_WINDOW,
      },
      { [RATE_LIMIT_BYPASS_HEADER]: "1" },
    );
  }
}

export function __resetForTests(): void {
  pathBuckets.clear();
  blanketBucket.length = 0;
  toastedAt.clear();
  posthoggedAt.clear();
  slackedAt.clear();
  sweepCounter = 0;
}

export const __testInternals = {
  WINDOW_MS,
  MAX_PER_PATH_PER_WINDOW,
  MAX_PER_USER_PER_WINDOW,
  TOAST_DEDUP_MS,
  POSTHOG_DEDUP_MS,
  SLACK_DEDUP_MS,
  SWEEP_EVERY,
  pathBuckets,
  toastedAt,
  posthoggedAt,
  slackedAt,
};
